import { array, object, optional, string } from 'zod';

import {
  countQuizText,
  hasFormulaOverLength,
  ITEM_FORMULA_LATEX_MAX_LENGTH,
} from '@/shared/features/rich-text/utils/countQuizText';

import { hasQuizItemContent } from './quizItemContent';

export const MIN_ORDER_ITEMS = 2;
export const MAX_ORDER_ITEMS = 5;

export const ORDER_ITEM_CONTENT_REQUIRED_MESSAGE =
  'Order item text, equation or image is required';
export const ORDER_ITEM_TEXT_MAX_LENGTH = 50;

/** ORDER 보기의 줄바꿈은 길이 제한과 저장값에서 제외한다. */
export const normalizeOrderItemText = (text: string) =>
  text.replace(/[\r\n]+/g, '');

/** TipTap이 줄바꿈으로 직렬화한 문단·br 경계를 제거한다. */
export const normalizeOrderItemRichText = (richText: string) =>
  richText.replace(/<\/p>\s*<p(?:\s[^>]*)?>/gi, '').replace(/<br\s*\/?>/gi, '');

/** 줄바꿈은 빼고 센다 — 저장 전에 지워지는 문자라서다. 수식은 `countQuizText` 규칙을 따른다. */
export const getOrderItemTextLength = (text: string) =>
  countQuizText(normalizeOrderItemText(text));

/**
 * 순서 맞추기(order) 보기 항목.
 *
 * 서버 `OrderSet.items` 와 같은 `Item` 모양이다. MATCH 와 동일하게 텍스트가 비어도
 * 수식·이미지 중 하나가 있으면 유효하다 — 서버도 "보기 콘텐츠가 하나도 없음"만 거부한다.
 */
const OrderItemSchema = object({
  id: string().min(1, 'Order item id is required'),
  text: string().refine(
    text => getOrderItemTextLength(text) <= ORDER_ITEM_TEXT_MAX_LENGTH,
    'Order item text must be at most 50 characters',
  ),
  richText: optional(string()),
  equation: optional(string()),
  imageUrl: optional(string()),
})
  .refine(hasQuizItemContent, {
    message: ORDER_ITEM_CONTENT_REQUIRED_MESSAGE,
    path: ['text'],
  })
  .refine(item => !hasFormulaOverLength(item, ITEM_FORMULA_LATEX_MAX_LENGTH), {
    message: 'Order item formula must be at most 100 characters',
    path: ['text'],
  });

/**
 * 폼은 `items` **배열 순서 자체를 정답 순서로** 취급한다.
 *
 * 서버 계약의 `correctOrderIds` 를 폼에 따로 두지 않는 이유:
 * 두 곳에 순서가 존재하면 드래그 재정렬·삽입·삭제마다 동기화가 필요하고,
 * 어긋나는 순간 전부 오답이 되는 조용한 버그가 된다. 직렬화 시점에
 * `items.map(item => item.id)` 로 파생시키면 desync 자체가 불가능하다.
 * 서버가 복수 정답 순서를 지원하지 않으므로 표현력 손실도 없다.
 *
 * @see views/build/util/buildFormToApiTransformer.ts
 */
export const QuizOrderDataSchema = object({
  items: array(OrderItemSchema).min(MIN_ORDER_ITEMS).max(MAX_ORDER_ITEMS),
}).refine(
  data => new Set(data.items.map(item => item.id)).size === data.items.length,
  {
    message: 'Every order item must have a unique id',
    path: ['items'],
  },
);
